import React, { useCallback, useEffect, useState } from "react";
import { Box, Stack, CircularProgress } from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { useNotify } from "../NotificationProvider";
import { getLetterHistory, redownloadLetter } from "../../services/letter";
import { pressable } from "../pressableProps";
import { tokens } from "../../theme";

const LABEL = {
  offer: "Offer letter",
  relieving: "Relieving letter",
  settlement: "Full and final settlement",
};

const when = (d) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

const personName = (p) => [p?.fname, p?.lname].filter(Boolean).join(" ") || "—";

/**
 * Every letter issued to this person.
 *
 * Re-download regenerates from what was recorded at the time, so a letter
 * downloaded today reads exactly as the one sent in March — not as the
 * employee's details are now. That is the point of keeping the snapshot
 * rather than the file.
 *
 * `refreshKey` lets the parent say "a letter was just issued" without this
 * polling for it.
 */
const LetterHistory = ({ employeeId, refreshKey = 0 }) => {
  const notify = useNotify();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    if (!employeeId) return;
    setLoading(true);
    getLetterHistory(employeeId)
      .then((res) => setRows(Array.isArray(res?.data) ? res.data : []))
      // Context, not the task. A failure here must not take the profile down.
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [employeeId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const download = async (row) => {
    setBusyId(row._id);
    try {
      const res = await redownloadLetter(row._id);
      if (!res?.success || !res.blob) {
        notify.error(res?.message || "Could not rebuild the letter");
        return;
      }
      const url = URL.createObjectURL(res.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename || `${LABEL[row.type]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      notify.error(e?.message || "Could not rebuild the letter");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <CircularProgress size={18} />;

  if (!rows.length) {
    return (
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
        No letters have been issued to this employee.
      </Box>
    );
  }

  return (
    <Stack spacing={1.25}>
      {rows.map((row) => (
        <Stack
          key={row._id}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ borderLeft: `2px solid ${tokens.border}`, pl: "0.75rem" }}
        >
          <Box>
            <Box sx={{ fontSize: "0.875rem", color: tokens.text }}>{LABEL[row.type] || row.type}</Box>
            <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>
              {when(row.createdAt)} · {personName(row.issuedBy)}
              {/* Emailed and downloaded are different facts. A letter handed
                  over in person was still issued, and saying "sent" of it
                  would be wrong. */}
              {row.emailedTo ? ` · emailed to ${row.emailedTo}` : " · downloaded"}
            </Box>
          </Box>
          <Box
            {...pressable(() => download(row))}
            aria-label={`Download the ${LABEL[row.type] || row.type} again`}
            sx={{
              display: "flex", alignItems: "center", gap: "0.3rem",
              fontSize: "0.8125rem", color: tokens.accent, cursor: "pointer",
              opacity: busyId === row._id ? 0.5 : 1,
            }}
          >
            <DownloadOutlinedIcon sx={{ fontSize: "1rem" }} />
            {busyId === row._id ? "Rebuilding…" : "Download"}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};

export default LetterHistory;
